import React, { useEffect, useState } from 'react'
import api from '../../api/api'
import {dateToDateFormat, dateToTime} from '../../utils/Date.js'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";

const Timesheet = () => {
  const [timeSheet, setTimeSheet] = useState([])
  const [rosterTotalHours, setRosterTotalHours] = useState(0)
  const [actualTotalHours, setActualTotalHours] = useState(0)
  const [loading, setLoading] = useState(true)
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date()
    const dayOfWeek = today.getDay()
    // Set to Monday of current week (subtract days to get to Monday)
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
    return new Date(today.setDate(diff))
  })

  // Helper function to format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0]
  }

  // Check if the current week is the actual current week
  const isCurrentWeek = () => {
    const today = new Date()
    const dayOfWeek = today.getDay()
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
    const thisWeekStart = new Date(today.setDate(diff))
    return currentWeekStart.toDateString() === thisWeekStart.toDateString()
  }

  // Add this new function to get the start of the current week
  const getCurrentWeekStart = () => {
    const today = new Date()
    const dayOfWeek = today.getDay()
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
    return new Date(today.setDate(diff))
  }

  const getTimeSheet = async (weekStartDate) => {
    try {
      setLoading(true)
      const response = await api.get(`/employee/timesheet/${formatDateForAPI(weekStartDate)}`)
      setTimeSheet(response.data.weeklyData)
      setRosterTotalHours(response.data.rosterTotalHours)
      setActualTotalHours(response.data.actualTotalHours)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getTimeSheet(currentWeekStart)
  }, [currentWeekStart])

  const handlePreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart)
    newWeekStart.setDate(newWeekStart.getDate() - 7)
    setCurrentWeekStart(newWeekStart)
  }

  const handleNextWeek = () => {
    if (!isCurrentWeek()) {
      const newWeekStart = new Date(currentWeekStart)
      newWeekStart.setDate(newWeekStart.getDate() + 7)
      setCurrentWeekStart(newWeekStart)
    }
  }

  const getWeekRange = () => {
    const weekEnd = new Date(currentWeekStart)
    weekEnd.setDate(weekEnd.getDate() + 6)
    return `${dateToDateFormat(currentWeekStart)} - ${dateToDateFormat(weekEnd)}`
  }

  if (loading) {
    return (
      <div>
        <div className="text-center">
          <div role="status">
            <svg
              aria-hidden="true"
              className="inline w-10 h-10 text-gray-200 animate-spin fill-indigo-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex sm:items-center md:justify-evenly justify-between ">
          <h1 className="text-base font-semibold text-gray-900">
            <p>{getWeekRange()}</p>
          </h1>

          <div className="relative flex items-center rounded-md bg-white shadow-sm md:items-stretch">
              <button
                type="button"
                onClick={handlePreviousWeek}
                className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
              >
                <span className="sr-only">Previous week</span>
                <ChevronLeftIcon className="size-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentWeekStart(getCurrentWeekStart())}
                className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
              >
                Current Week
              </button>
              <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
              <button
                type="button"
                onClick={handleNextWeek}
                disabled={isCurrentWeek()}
                className={`flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50
                  ${isCurrentWeek() ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className="sr-only">Next week</span>
                <ChevronRightIcon className="size-5" aria-hidden="true" />
              </button>
            </div>
      </div>
      <div className="-mx-4 mt-8 flow-root sm:mx-0">
        <table className="min-w-full">
          <thead className="border-b border-gray-300 text-gray-900">
            <tr>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                Date
              </th>
              <th
                scope="col"
                className="hidden px-3 py-3.5 text-right text-sm font-semibold text-gray-900 sm:table-cell"
              >
                Roster Start
              </th>
              <th
                scope="col"
                className="hidden px-3 py-3.5 text-right text-sm font-semibold text-gray-900 sm:table-cell"
              >
                Roster End
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 sm:table-cell"
              >
                Actual Start
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 sm:table-cell"
              >
                Actual End
              </th>
              <th scope="col" className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-0">
                Total Hours
              </th>
            </tr>
          </thead>
          <tbody>
            {timeSheet
              .filter((day) => day.rosterStart || day.actualStart)
              .map((day) => (
                <tr key={day.date} className="border-b border-gray-200">
                  <td className="max-w-0 py-5 pl-4 text-sm sm:pl-0">
                    <div className="font-medium text-gray-900">{dateToDateFormat(day.date)}</div>
                  </td>
                  <td className="hidden px-3 py-5 text-right text-sm text-gray-500 sm:table-cell">
                    {day.rosterStart && dateToTime(day.rosterStart)}
                  </td>
                  <td className="hidden px-3 py-5 text-right text-sm text-gray-500 sm:table-cell">
                    {day.rosterEnd && dateToTime(day.rosterEnd)}
                  </td>
                  <td className="px-3 py-5 text-right text-sm text-gray-500 sm:table-cell">
                    {day.actualStart && dateToTime(day.actualStart)}
                  </td>
                  <td className="px-3 py-5 text-right text-sm text-gray-500 sm:table-cell">
                    {day.actualEnd && dateToTime(day.actualEnd)}
                  </td>
                  <td className="py-5 pl-3 pr-4 text-right text-sm text-gray-500 sm:pr-0">{day.totalHours}</td>
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr>
              <th
                scope="row"
                colSpan={5}
                className="hidden pl-4 pr-3 pt-6 text-right text-sm font-normal text-gray-500 sm:table-cell sm:pl-0"
              >
                Roster total hours
              </th>
              <th scope="row" className="pl-4 pr-3 pt-6 text-left text-sm font-normal text-gray-500 sm:hidden">
                Roster total hours
              </th>
              <td className="pl-3 pr-4 pt-6 text-right text-sm text-gray-500 sm:pr-0">{rosterTotalHours}</td>
            </tr>
            <tr>
              <th
                scope="row"
                colSpan={5}
                className="hidden pl-4 pr-3 pt-4 text-right text-sm font-semibold text-gray-900 sm:table-cell sm:pl-0"
              >
                Actual total hours
              </th>
              <th scope="row" className="pl-4 pr-3 pt-4 text-left text-sm font-semibold text-gray-900 sm:hidden">
                Actual total hours
              </th>
              <td className="pl-3 pr-4 pt-4 text-right text-sm font-semibold text-gray-900 sm:pr-0">{actualTotalHours}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

export default Timesheet